import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigationType, useSearchParams } from "react-router-dom";

/*
 * List state that lives in the URL.
 *
 * The query string is the source of truth for a list's search, filters, sort,
 * tab, page and pickers: opening a URL restores the exact state, a shared link
 * works, and Back from a detail page lands on the list as it was. Screens read
 * `values` and fetch off them, so the fetch and the URL cannot disagree.
 *
 * - Only non-default values are written (no `?status=&page=1` noise).
 * - Every change REPLACES the history entry: Back leaves the list rather than
 *   undoing filters one by one. Navigating to a detail page is a normal push.
 * - Changing anything other than the page resets the page to 1.
 * - A value that fails to parse (unknown option, bad date, NaN) silently falls
 *   back to its default. Values are ids/keys, never translated labels.
 * - Fields marked `debounce` have a `draft` the input binds to; the URL (and so
 *   the fetch) follows ~400ms after typing stops, trimmed, empty removed.
 */

const DATE = /^\d{4}-\d{2}-\d{2}$/;

const isValidDate = (value) => {
  if (!DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

/** Field types: each parses a raw query value (string | null) to a typed value. */
export const param = {
  /** Free text, trimmed. `debounce` (ms) gives it a draft for an input. */
  string: (fallback = "", { debounce } = {}) => ({
    fallback,
    debounce,
    parse: (raw) => (raw == null ? fallback : raw.trim() || fallback),
  }),
  /** One of a fixed set of keys. */
  oneOf: (values, fallback = "") => ({
    fallback,
    parse: (raw) => (raw != null && values.includes(raw) ? raw : fallback),
  }),
  /** A positive integer, e.g. a page number. */
  int: (fallback = 1, { min = 1 } = {}) => ({
    fallback,
    parse: (raw) => {
      const n = Number.parseInt(raw ?? "", 10);
      return Number.isInteger(n) && n >= min ? n : fallback;
    },
  }),
  /** A flag, written as `1`. */
  flag: (fallback = false) => ({
    fallback,
    parse: (raw) => (raw == null ? fallback : raw === "1" || raw === "true"),
    write: (value) => (value ? "1" : null),
  }),
  /** A calendar date, YYYY-MM-DD. `fallback` may be a function (e.g. today). */
  date: (fallback = "") => ({
    fallback,
    parse: (raw) =>
      raw != null && isValidDate(raw) ? raw : typeof fallback === "function" ? fallback() : fallback,
  }),
  /** A list of ids, comma-separated in the URL. */
  list: () => ({
    fallback: [],
    parse: (raw) => (raw ? raw.split(",").filter(Boolean) : []),
    write: (value) => (value?.length ? value.join(",") : null),
  }),
};

const fallbackOf = (field) => (typeof field.fallback === "function" ? field.fallback() : field.fallback);

const sameValue = (a, b) =>
  Array.isArray(a) || Array.isArray(b) ? JSON.stringify(a ?? []) === JSON.stringify(b ?? []) : a === b;

/** The query-string form of one value, or null to leave it out. */
const serialise = (field, value) => {
  if (value == null || sameValue(value, fallbackOf(field))) return null;
  if (field.write) return field.write(value);
  const text = String(value).trim();
  return text === "" ? null : text;
};

/**
 * @param {Record<string, ReturnType<typeof param[keyof typeof param]>>} schema
 *        Keys are the URL parameter names. May change between renders (e.g. a
 *        list of allowed ids that arrives later); the latest one is used.
 * @param {{ pageKey?: string }} options  The key reset to its default whenever
 *        anything else changes. Defaults to "page"; null to disable.
 */
export default function useUrlState(schema, { pageKey = "page" } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  const query = searchParams.toString();

  // Parsed every render (the schema may carry ids that arrived since), but
  // handed out with a stable identity until a value actually changes — the
  // screens' fetches depend on it.
  const parsed = Object.fromEntries(
    Object.entries(schema).map(([key, field]) => [key, field.parse(new URLSearchParams(query).get(key))])
  );
  const signature = JSON.stringify(parsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const values = useMemo(() => parsed, [signature]);

  /** Merge changes into the URL (replace). `changes` may be a function of the current values. */
  const set = useCallback(
    (changes) => {
      setSearchParams(
        (current) => {
          const fields = schemaRef.current;
          const parsed = Object.fromEntries(
            Object.entries(fields).map(([key, field]) => [key, field.parse(current.get(key))])
          );
          const patch = typeof changes === "function" ? changes(parsed) : changes;
          const next = new URLSearchParams(current);

          let filterChanged = false;
          Object.entries(patch).forEach(([key, value]) => {
            const field = fields[key];
            if (!field) return;
            if (key !== pageKey && !sameValue(value, parsed[key])) filterChanged = true;
            const text = serialise(field, value);
            if (text == null) next.delete(key);
            else next.set(key, text);
          });

          if (filterChanged && pageKey && fields[pageKey] && !(pageKey in patch)) next.delete(pageKey);

          return next;
        },
        { replace: true }
      );
    },
    [pageKey, setSearchParams]
  );

  // Debounced fields: the input edits a draft; the URL follows after a pause.
  const debounced = Object.entries(schema).filter(([, field]) => field.debounce);
  const debouncedKeys = debounced.map(([key]) => key).join(",");
  const [draft, setDraftState] = useState(() =>
    Object.fromEntries(debounced.map(([key]) => [key, values[key]]))
  );
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const timers = useRef({});
  // What this hook itself last wrote per field, so its own write is not
  // mistaken for an outside change (which would clobber what was typed since).
  const written = useRef({});

  // The URL moved on its own (Back/Forward, reset, a link): the inputs follow.
  useEffect(() => {
    setDraftState((current) => {
      let changed = false;
      const next = { ...current };
      debouncedKeys
        .split(",")
        .filter(Boolean)
        .forEach((key) => {
          if (key in written.current && written.current[key] === values[key]) return;
          delete written.current[key];
          if ((current[key] ?? "").trim() !== values[key]) {
            next[key] = values[key];
            changed = true;
          }
        });
      return changed ? next : current;
    });
  }, [debouncedKeys, values]);

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const setDraft = useCallback(
    (key, text) => {
      setDraftState((current) => ({ ...current, [key]: text }));
      clearTimeout(timers.current[key]);
      const wait = schemaRef.current[key]?.debounce ?? 400;
      timers.current[key] = setTimeout(() => {
        written.current[key] = text.trim();
        set({ [key]: text.trim() });
      }, wait);
    },
    [set]
  );

  /** Back to every default (keeps any parameter this schema does not own). */
  const reset = useCallback(() => {
    // Typed-but-unsent search text goes too, not just what reached the URL.
    Object.values(timers.current).forEach(clearTimeout);
    setDraftState((current) => Object.fromEntries(Object.keys(current).map((key) => [key, ""])));
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        Object.keys(schemaRef.current).forEach((key) => next.delete(key));
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  /** Apply a draft now (Enter / a submit button) instead of waiting. */
  const flush = useCallback(
    (key) => {
      clearTimeout(timers.current[key]);
      const text = (draftRef.current[key] ?? "").trim();
      written.current[key] = text;
      set({ [key]: text });
    },
    [set]
  );

  return { values, set, reset, draft, setDraft, flush };
}

/*
 * Scroll restoration for a list inside the portal's scrolling main area.
 *
 * The position is saved per history entry while the user scrolls; on a Back /
 * Forward (POP) arrival it is put back once the list has rendered (`ready`),
 * so the rows exist to scroll to.
 */
const scrollRoot = () => document.querySelector("[data-scroll-root]");
const storageKey = (key) => `scroll:${key}`;

export function useScrollRestoration(ready) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const restored = useRef(null);

  useEffect(() => {
    const root = scrollRoot();
    if (!root) return undefined;

    let frame = null;
    const save = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        try {
          sessionStorage.setItem(storageKey(location.key), String(root.scrollTop));
        } catch {
          // Storage unavailable: nothing to restore later, nothing breaks.
        }
      });
    };

    root.addEventListener("scroll", save, { passive: true });
    return () => {
      root.removeEventListener("scroll", save);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [location.key]);

  useEffect(() => {
    if (!ready || navigationType !== "POP" || restored.current === location.key) return;
    restored.current = location.key;

    const root = scrollRoot();
    let saved = null;
    try {
      saved = sessionStorage.getItem(storageKey(location.key));
    } catch {
      saved = null;
    }
    if (root && saved != null) root.scrollTop = Number(saved) || 0;
  }, [ready, navigationType, location.key]);
}

/** The yes / no / any filters most lists share: `1`, `0`, or absent. */
export const yesNo = () => param.oneOf(["1", "0"]);

/**
 * The common shape of a paginated, filterable list: `filterSchema` plus a page.
 * Returns what the list hooks used to keep in state, now read from the URL.
 * `search` (when in the schema) is debounced: bind the input to
 * `searchTerm` / `setSearchTerm`, and call `submitSearch()` on Enter.
 */
export function useListUrlState(filterSchema) {
  const url = useUrlState({ ...filterSchema, page: param.int(1) });
  const { values, set, reset, draft, setDraft, flush } = url;

  const filters = useMemo(() => {
    const { page, ...rest } = values;
    return rest;
  }, [values]);

  const setPage = useCallback(
    (next) => set((current) => ({ page: typeof next === "function" ? next(current.page) : next })),
    [set]
  );

  const setSearchTerm = useCallback((text) => setDraft("search", text), [setDraft]);
  const submitSearch = useCallback(() => flush("search"), [flush]);

  return {
    page: values.page,
    setPage,
    filters,
    applyFilters: set,
    resetFilters: reset,
    searchTerm: draft.search ?? "",
    setSearchTerm,
    submitSearch,
  };
}

/**
 * Once the allowed options are known (e.g. the branches this user can reach),
 * a URL value that is not among them silently falls back to the default.
 * `options` is null/undefined while still loading.
 */
export function useValidOption(value, options, clear) {
  useEffect(() => {
    if (!value || !options) return;
    const ids = options.map((option) => (typeof option === "object" ? String(option.id) : String(option)));
    if (!ids.includes(String(value))) clear();
  }, [value, options, clear]);
}
